import React, {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {UserDTO, UserType, ExecutionSetupDTO} from 'hemwb-api';
import {Form, useForm, UserSelectHandler} from '../../../../../core/Form';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from 'react-reactive-form';
import {tid} from '../../../../../../testUtils';
import {Button, Grid, IconButton} from '@mui/material';
import AutocompleteUserControl from '../../../../../core/Form/AutocompleteUserControl';
import CloseIcon from '@mui/icons-material/Close';
import GridItem from '../../../../../core/Form/GridItem';
import {UserCard} from '../../../../../core/UserCard/UserCard';
import {useCurrentUser} from '../../../../../../store/auth';
import {messageAdd, MessageTypes} from '../../../../../../store/message';
import {ModelDTO} from 'hemwb-api';
import {getModelSteward, modifyModelMetadata} from '../../../../../../store/model';
import {fullName} from '../../../../../../store/user/utils';
import {useDispatch} from 'react-redux';
import {validateModelStewardControl} from './utils';
import {canEditTHModelAccess} from '../../../../../../permissions/usePermissions';
import ActionHeader from '../../../../../core/Header/ActionHeader/ActionHeader';
import {useSideBarModal} from '../../../../../layout/common/useSideBarModal';
import SideBarModal from '../../../../../layout/common/SideBarModal';
import UserSettingsForm from './UserSettingsForm';

export type ModelEditPermissionsFormData = {
  model: ModelDTO;
  viewers: UserDTO[];
  displayViewers: UserDTO[];
  owners: UserDTO[];
  calibrationModelers: UserDTO[];
  scenarioModelers: UserDTO[];
  vestLead?: UserDTO;
};

type ModelEditPermissionsFormValues = {
  viewers: UserDTO[];
  displayViewers: UserDTO[];
  owners: UserDTO[];
  calibrationModelers: UserDTO[];
  scenarioModelers: UserDTO[];
  vestLead: UserDTO[];
  modelSteward: UserDTO[];
};

const dataToValues = ({
  model,
  viewers,
  displayViewers,
  owners,
  calibrationModelers,
  scenarioModelers,
  vestLead,
}: ModelEditPermissionsFormData) => ({
  viewers,
  displayViewers,
  owners,
  calibrationModelers,
  scenarioModelers,
  vestLead: [vestLead].filter(Boolean),
  modelSteward: [getModelSteward(model)].filter(Boolean),
});

const valuesToData = (
  {
    owners,
    calibrationModelers,
    scenarioModelers,
    viewers,
    displayViewers,
    vestLead,
    modelSteward,
  }: ModelEditPermissionsFormValues,
  data: ModelEditPermissionsFormData,
): ModelEditPermissionsFormData => {
  return {
    vestLead: vestLead?.[0],
    owners,
    calibrationModelers,
    scenarioModelers,
    viewers,
    displayViewers,
    model: modifyModelMetadata(data.model, {
      isid_of_model_steward: (modelSteward[0] && modelSteward[0].id) || '',
      email_for_model_steward: (modelSteward[0] && modelSteward[0].email) || '',
      name_of_model_steward: (modelSteward[0] && fullName(modelSteward[0])) || '',
    }),
  };
};

type ModelEditPermissionsFormProps = {
  onSubmit: (values: ModelEditPermissionsFormData) => void;
  onChange?: (values: ModelEditPermissionsFormData) => void;
  data: ModelEditPermissionsFormData;
  buttons: (params: FormGroup & {submitting: boolean}) => string | ReactNode;
  model?: ModelDTO;
  pneuInsightsModel?: boolean;
  externalModel?: boolean;
  theoremModel?: boolean;
  webModel?: boolean;
  disableUserSettings?: boolean;
  storedCalibrationList?: ExecutionSetupDTO[] | null;
  storedScenarioList?: ExecutionSetupDTO[] | null;
};

const userFindOptions = {limit: 10, includeNpa: true, includeUnenrolled: true};

const ModelEditPermissionsForm: React.FC<ModelEditPermissionsFormProps> = ({
  buttons,
  onSubmit,
  onChange,
  data,
  model,
  externalModel,
  pneuInsightsModel,
  theoremModel,
  webModel,
  disableUserSettings,
  storedCalibrationList,
  storedScenarioList,
}) => {
  const {buttonRef, openModal, closeModal, user} = useSideBarModal();
  const dispatch = useDispatch();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const initialStewardValidation = useRef<Promise<ValidationErrors | null>>();
  const {id: currentUserId} = useCurrentUser();
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState<boolean>(false);
  const [viewerDropdownOpen, setViewerDropdownOpen] = useState<boolean>(false);
  const [calibrationModelerDropdownOpen, setCalibrationModelerDropdownOpen] = useState<boolean>(false);
  const [scenarioDropdownOpen, setScenarioDropdownOpen] = useState<boolean>(false);
  const [displayViewerDropdownOpen, setDisplayViewerDropdownOpen] = useState<boolean>(false);

  const form = useForm(
    new FormGroup({
      viewers: new FormControl([]),
      displayViewers: new FormControl([]),
      calibrationModelers: new FormControl([]),
      scenarioModelers: new FormControl([]),
      owners: new FormControl([], Validators.required),
      vestLead: new FormControl([], Validators.required),
      modelSteward: new FormControl(
        null,
        [
          (control) => {
            const value = control?.value?.[0] as UserDTO;
            return !value || value.id === undefined ? {required: true} : null;
          },
        ],
        (control: AbstractControl) => {
          if (control.dirty) {
            return Promise.resolve(null);
          } else if (initialStewardValidation.current === undefined) {
            initialStewardValidation.current = validateModelStewardControl(control, dispatch);
          }
          return initialStewardValidation.current;
        },
      ),
    }),
    dataToValues(data),
  );

  useEffect(() => {
    if (form) {
      const update = () => setTimeout(() => onChange?.(valuesToData(form.value, data)), 100);
      if (pneuInsightsModel) {
        form.disable();
      }

      const removeUsersFromFieldByField = (removeFromField: string, byField: string) => {
        const removeFromControl = form.get(removeFromField);
        const removeByControl = form.get(byField);
        const idsToRemove = (removeFromControl.value || [])
          .map((u: UserDTO) => u.id)
          .filter((id: UserDTO['id']) => (removeByControl.value || []).some((o: UserDTO) => o.id === id));

        if (idsToRemove.length) {
          removeFromControl.setValue(removeFromControl.value.filter((u: UserDTO) => !idsToRemove.includes(u.id)));
        }
      };

      const handleCalibrationModelersChanged = () => {
        removeUsersFromFieldByField('scenarioModelers', 'calibrationModelers');
      };

      const handleModelStewardChanged = () => {
        removeUsersFromFieldByField('calibrationModelers', 'modelSteward');
        removeUsersFromFieldByField('scenarioModelers', 'modelSteward');
        removeUsersFromFieldByField('viewers', 'modelSteward');
        removeUsersFromFieldByField('displayViewers', 'modelSteward');
      };

      const handleViewersChanged = (user: UserDTO) => {
        if (!user.active) {
          return null;
        }
        removeUsersFromFieldByField('calibrationModelers', 'viewers');
        removeUsersFromFieldByField('scenarioModelers', 'viewers');
        removeUsersFromFieldByField('displayViewers', 'viewers');
      };

      const handleDisplayViewersChanged = () => {
        removeUsersFromFieldByField('calibrationModelers', 'displayViewers');
        removeUsersFromFieldByField('scenarioModelers', 'displayViewers');
        removeUsersFromFieldByField('viewers', 'displayViewers');
      };

      const handleOwnersChanged = () => {
        removeUsersFromFieldByField('calibrationModelers', 'owners');
        removeUsersFromFieldByField('scenarioModelers', 'owners');
        removeUsersFromFieldByField('viewers', 'owners');
        removeUsersFromFieldByField('displayViewers', 'owners');
      };

      const onlyLastVestLead = () => {
        if (form.controls.vestLead.value.length > 1) {
          if (
            form.controls.vestLead.value[form.controls.vestLead.value.length - 1].userType === UserType.EXTERNALUSER
          ) {
            form.controls.vestLead.setValue([form.controls.vestLead.value[0]]);
            dispatch(
              messageAdd('External user cannot be added as the Value and Implementation Lead.', MessageTypes.ERROR),
            );
          } else {
            form.controls.vestLead.setValue([form.controls.vestLead.value[form.controls.vestLead.value.length - 1]]);
          }
        } else {
          if (
            form.controls.vestLead.value.length === 1 &&
            form.controls.vestLead.value[0].userType === UserType.EXTERNALUSER
          ) {
            form.controls.vestLead.setValue([]);
            dispatch(
              messageAdd('External user cannot be added as the Value and Implementation Lead.', MessageTypes.ERROR),
            );
          }
        }
      };
      const onlyLastSteward = () => {
        if (form.controls.modelSteward.value.length > 1) {
          if (
            form.controls.modelSteward.value[form.controls.modelSteward.value.length - 1].userType ===
            UserType.EXTERNALUSER
          ) {
            form.controls.modelSteward.setValue([form.controls.modelSteward.value[0]]);
            dispatch(messageAdd('External user cannot be added as the HEDS Lead.', MessageTypes.ERROR));
          } else {
            form.controls.modelSteward.setValue([
              form.controls.modelSteward.value[form.controls.modelSteward.value.length - 1],
            ]);
          }
        } else {
          if (
            form.controls.modelSteward.value.length === 1 &&
            form.controls.modelSteward.value[0].userType === UserType.EXTERNALUSER
          ) {
            form.controls.modelSteward.setValue([]);
            dispatch(messageAdd('External user cannot be added as the HEDS Lead.', MessageTypes.ERROR));
          }
        }
        handleModelStewardChanged();
      };

      form.valueChanges.subscribe(update);

      form.controls.owners.valueChanges.subscribe(handleOwnersChanged);
      form.controls.viewers.valueChanges.subscribe(handleViewersChanged);
      form.controls.displayViewers.valueChanges.subscribe(handleDisplayViewersChanged);
      form.controls.calibrationModelers.valueChanges.subscribe(handleCalibrationModelersChanged);

      form.controls.vestLead.valueChanges.subscribe(onlyLastVestLead);
      form.controls.modelSteward.valueChanges.subscribe(onlyLastSteward);
    }
  }, [form]);

  const handleCancelSubModelIds = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleUpdateSubModelIds = useCallback(
    (id: number, selected: boolean) => {
      const oldSelection = selectedIds;
      if (selected) {
        if (!oldSelection.includes(id)) {
          oldSelection.push(id);
        }
      } else {
        const index = oldSelection.indexOf(id);
        if (index !== -1) {
          oldSelection.splice(index, 1);
        }
      }
      setSelectedIds(oldSelection);
    },
    [selectedIds],
  );

  const handleSecurityAPIError = useCallback(() => {
    const viewersValue = form.controls.viewers.value;
    viewersValue.pop();
    form.controls.viewers.setValue(viewersValue);
  }, [form]);

  const restrictAccessOfExternalUser = useCallback(
    (name: string) => {
      if (!['modelSteward', 'vestLead'].includes(name)) {
        const roleDetails = form.controls[name].value;
        roleDetails.pop();
        form.controls[name].setValue(roleDetails);
      }
    },
    [form.controls],
  );

  const handleDelete = useCallback(
    (user: UserDTO, formFieldName: string) => {
      const control = form.get(formFieldName);
      control.setValue(control.value.filter((u: UserDTO) => u.id !== user.id));
      control.markAsTouched();
    },
    [form],
  );
  const disableCheckbox = useCallback(() => {
    const userIds: string[] = [];
    form.controls.owners.value.map((user: any) => userIds.push(user.id));
    if (form.controls.vestLead.value) {
      userIds.push(form.controls.vestLead.value[0]?.id);
    }
    if (form.controls.modelSteward.value) {
      userIds.push(form.controls.modelSteward.value[0]?.id);
    }

    if (user) {
      const isUserPresent = userIds.includes(user.id);
      return isUserPresent;
    }
  }, [form.controls.modelSteward.value, form.controls.owners.value, form.controls.vestLead.value, user]);
  const Modal = useMemo(() => {
    return (
      <SideBarModal
        className="whiteTheme"
        isOpen={!!buttonRef}
        buttonRef={buttonRef}
        closeModal={closeModal}
        height={613}
      >
        <div style={{width: '60rem'}}>
          {!!buttonRef && user && (
            <UserSettingsForm
              user={user}
              modelUuid={data.model?.uuid}
              disableCheckbox={disableCheckbox}
              isOpen={!!buttonRef}
              closeModal={closeModal}
              buttonRef={buttonRef}
              handleUpdateSubModelIds={handleUpdateSubModelIds}
              handleCancelSubModelIds={handleCancelSubModelIds}
              storedCalibrationList={storedCalibrationList}
              storedScenarioList={storedScenarioList}
              selectedIds={selectedIds}
            />
          )}
        </div>
      </SideBarModal>
    );
  }, [
    buttonRef,
    closeModal,
    user,
    selectedIds,
    handleUpdateSubModelIds,
    handleCancelSubModelIds,
    disableCheckbox,
    storedCalibrationList,
    storedScenarioList,
  ]);

  const displayExpertiseLevel = (user: UserDTO) => {
    return webModel && !disableUserSettings ? (
      <>
        <span style={{display: data.model.uuid ? 'inline' : 'none'}}>
          <Button
            variant="outlined"
            color="primary"
            //className="MuiButtonBase-root MuiButton-root MuiButton-outlined MuiButton-outlinedPrimary MuiButton-outlinedSizeSmall MuiButton-sizeSmall"
            onClick={(event) => openModal(event, user)}
            style={{display: 'flow-root', fontSize: '1.36rem'}}
          >
            User Settings
          </Button>

          {Modal}
        </span>
      </>
    ) : (
      <></>
    );
  };

  const dropdownOption = (props: any, option: any) => {
    return <li {...props} style={{cursor: 'pointer'}}>{`${fullName(option)} ${option.email}`}</li>;
  };

  const handleSelectedOwner: UserSelectHandler<UserDTO> = useCallback(
    (user: UserDTO) => {
      const currentOwners = form.controls.owners.value || [];
      form.controls.owners.setValue([...currentOwners, user]);
      form.controls.owners.markAsTouched();
      setOwnerDropdownOpen(false);
    },
    [form],
  );

  const handleSelectedViewer: UserSelectHandler<UserDTO> = useCallback(
    (user: UserDTO) => {
      const currentViewers = form.controls.viewers.value || [];
      form.controls.viewers.setValue([...currentViewers, user]);
      form.controls.viewers.markAsTouched();
      setViewerDropdownOpen(false);
    },
    [form],
  );

  const handleSelectedCalibrationModelers: UserSelectHandler<UserDTO> = useCallback(
    (user: UserDTO) => {
      const currentCalibrationModelers = form.controls.calibrationModelers.value || [];
      form.controls.calibrationModelers.setValue([...currentCalibrationModelers, user]);
      form.controls.calibrationModelers.markAsTouched();
      setCalibrationModelerDropdownOpen(false);
    },
    [form],
  );

  const handleSelectedScenarioModelers: UserSelectHandler<UserDTO> = useCallback(
    (user: UserDTO) => {
      const currentScenarioModelers = form.controls.scenarioModelers.value || [];
      form.controls.scenarioModelers.setValue([...currentScenarioModelers, user]);
      form.controls.scenarioModelers.markAsTouched();
      setScenarioDropdownOpen(false);
    },
    [form],
  );

  const handleSelectedDisplayViewers: UserSelectHandler<UserDTO> = useCallback(
    (user: UserDTO) => {
      const currentDisplayViewers = form.controls.displayViewers.value || [];
      form.controls.displayViewers.setValue([...currentDisplayViewers, user]);
      form.controls.displayViewers.markAsTouched();
      setDisplayViewerDropdownOpen(false);
    },
    [form],
  );

  return (
    <Form
      FieldGroupProps={{strict: false}}
      group={form}
      onSubmit={() => onSubmit(valuesToData(form.value, data))}
      className=""
      {...tid('model-permissions-form')}
      render={(renderParams) => {
        return (
          <>
            <Grid
              container
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              style={{paddingLeft: '3rem'}}
            >
              <Grid container direction="column" style={{paddingRight: '3rem'}} xs={12} md={6}>
                <Grid>
                  <ActionHeader label={<strong>Provide</strong>}>
                    <strong className="wrapRelative">
                      Value and Implementation Lead{' '}
                      <span className="MuiFormLabel-asterisk MuiInputLabel-asterisk">*</span>
                    </strong>
                  </ActionHeader>
                </Grid>
                <GridItem>
                  <AutocompleteUserControl
                    disabled={theoremModel}
                    name="vestLead"
                    label="Find user"
                    userFindOptions={userFindOptions}
                    restrictAccessOfExternalUser={restrictAccessOfExternalUser}
                    model={model}
                    AutocompleteProps={{
                      disableClearable: true,
                      multiple: true,
                      renderTags: () => null,
                      getOptionDisabled: (option: UserDTO) =>
                        form.controls.vestLead.value?.some((u: UserDTO) => u.id === option.id),
                      renderOption: (props, option) => dropdownOption(props, option),
                    }}
                  />
                </GridItem>

                <Grid style={{width: '36.7rem', marginBottom: '2rem'}}>
                  {form.controls.vestLead.value && form.controls.vestLead.value[0] && (
                    <UserCard
                      style={{marginBottom: '0.8rem'}}
                      label="Value and Implementation Lead"
                      user={form.controls.vestLead.value[0]}
                      key={form.controls.vestLead.value[0].id}
                      actions={
                        !externalModel ? (
                          <>
                            <IconButton
                              style={{float: 'right'}}
                              aria-label="Remove"
                              size="small"
                              onClick={() => {
                                form.controls.vestLead.setValue([]);
                              }}
                              {...tid('vestLead-card-remove', form.controls.vestLead.value[0].id)}
                            >
                              <CloseIcon />
                            </IconButton>
                            {webModel && !disableUserSettings && (
                              <span style={{display: data.model.uuid ? 'inline' : 'none'}}>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  //className="MuiButtonBase-root MuiButton-root MuiButton-outlined MuiButton-outlinedPrimary MuiButton-outlinedSizeSmall MuiButton-sizeSmall"
                                  onClick={(event) => openModal(event, form.controls.vestLead.value[0])}
                                  //onSubmit={openModal}
                                  type="button"
                                  style={{display: 'flow-root', fontSize: '1.36rem'}}
                                >
                                  User Settings
                                </Button>

                                {Modal}
                              </span>
                            )}
                          </>
                        ) : (
                          <></>
                        )
                      }
                    />
                  )}
                </Grid>
              </Grid>
              <Grid container direction="column" style={{paddingRight: '3rem'}} xs={12} md={6}>
                <Grid>
                  <ActionHeader label={<strong>Provide</strong>}>
                    <strong className="wrapRelative">
                      HEDS Lead <span className="MuiFormLabel-asterisk MuiInputLabel-asterisk">*</span>
                    </strong>
                  </ActionHeader>
                </Grid>
                <GridItem>
                  <AutocompleteUserControl
                    name="modelSteward"
                    label="Find user"
                    restrictAccessOfExternalUser={restrictAccessOfExternalUser}
                    model={model}
                    disabled={theoremModel}
                    AutocompleteProps={{
                      disableClearable: true,
                      multiple: true,
                      renderTags: () => null,
                      getOptionDisabled: (option: UserDTO) =>
                        form.controls.modelSteward.value?.some((u: UserDTO) => u.id === option.id),
                      renderOption: (props, option) => dropdownOption(props, option),
                    }}
                  />
                </GridItem>

                <Grid style={{width: '36.7rem', marginBottom: '2rem'}}>
                  {form.controls.modelSteward.value && form.controls.modelSteward.value[0] && (
                    <UserCard
                      style={{marginBottom: '0.8rem'}}
                      label="HEDS Lead"
                      user={form.controls.modelSteward.value[0]}
                      key={form.controls.modelSteward.value[0].id}
                      actions={
                        !externalModel ? (
                          <IconButton
                            aria-label="Remove"
                            size="small"
                            onClick={() => {
                              form.controls.modelSteward.setValue([]);
                            }}
                            {...tid('modelSteward-card-remove', form.controls.modelSteward.value[0].id)}
                          >
                            <CloseIcon />
                          </IconButton>
                        ) : (
                          <></>
                        )
                      }
                    />
                  )}
                </Grid>
              </Grid>
              <Grid container direction="column" xs={12} md={6}>
                <Grid>
                  <ActionHeader label="Grant">
                    Add model Editor
                    <br />
                    (Editing capabilities)
                  </ActionHeader>
                </Grid>
                <GridItem>
                  <AutocompleteUserControl
                    name="owners"
                    label="Find User"
                    disabled={theoremModel}
                    userFindOptions={userFindOptions}
                    restrictAccessOfExternalUser={restrictAccessOfExternalUser}
                    onSelect={(user) => handleSelectedOwner(user as UserDTO)}
                    model={model}
                    AutocompleteProps={{
                      open: ownerDropdownOpen,
                      onOpen: () => setOwnerDropdownOpen(true),
                      onClose: () => setOwnerDropdownOpen(false),
                      disableClearable: true,
                      multiple: true,
                      renderTags: () => null,
                      getOptionDisabled: (option: UserDTO) =>
                        form.controls.owners.value?.some((u: UserDTO) => u.id === option.id),
                      //renderOption: (props, option) => dropdownOption(props, option),
                    }}
                  />
                </GridItem>
                <Grid style={{width: '36.7rem', marginBottom: '2rem'}}>
                  {form.controls.owners.value.map((user: UserDTO) => (
                    <UserCard
                      style={{marginBottom: '0.8rem'}}
                      label="Editor"
                      user={user}
                      key={user.id}
                      actions={
                        !externalModel &&
                        user.id !== currentUserId &&
                        user.id !== form.controls.vestLead.value?.[0]?.id ? (
                          <>
                            <IconButton
                              style={{float: 'right'}}
                              aria-label="Remove"
                              size="small"
                              onClick={() => handleDelete(user, 'owners')}
                              {...tid('user-card-remove', user.id)}
                            >
                              <CloseIcon />
                            </IconButton>
                            {webModel && !disableUserSettings && (
                              <span style={{display: data.model.uuid ? 'inline' : 'none'}}>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  //className="MuiButtonBase-root MuiButton-root MuiButton-outlined MuiButton-outlinedPrimary MuiButton-outlinedSizeSmall MuiButton-sizeSmall"
                                  onClick={(event) => openModal(event, user)}
                                  style={{display: 'flow-root', fontSize: '1.36rem'}}
                                >
                                  User Settings
                                </Button>

                                {Modal}
                              </span>
                            )}
                          </>
                        ) : (
                          displayExpertiseLevel(user)
                        )
                      }
                    />
                  ))}
                </Grid>
              </Grid>
              <Grid container direction="column" style={{paddingRight: '3rem'}} xs={12} md={6}>
                <Grid>
                  <ActionHeader label="Grant">
                    Permissions to view and
                    <br />
                    download this Model
                  </ActionHeader>
                </Grid>
                <GridItem>
                  <AutocompleteUserControl
                    name="viewers"
                    label="Model Viewers"
                    disabled={theoremModel && !canEditTHModelAccess(model)}
                    userFindOptions={userFindOptions}
                    handleSecurityAPI={handleSecurityAPIError}
                    restrictAccessOfExternalUser={restrictAccessOfExternalUser}
                    onSelect={(user) => handleSelectedViewer(user as UserDTO)}
                    model={model}
                    theoremModel={theoremModel}
                    AutocompleteProps={{
                      open: viewerDropdownOpen,
                      onOpen: () => setViewerDropdownOpen(true),
                      onClose: () => setViewerDropdownOpen(false),
                      disableClearable: true,
                      multiple: true,
                      renderTags: () => null,
                      getOptionDisabled: (option: UserDTO) =>
                        form.controls.modelSteward.value?.some((u: UserDTO) => u.id === option.id) ||
                        form.controls.owners.value?.some((u: UserDTO) => u.id === option.id) ||
                        form.controls.viewers.value?.some((u: UserDTO) => u.id === option.id),
                      //renderOption: (props, option) => dropdownOption(props, option),
                    }}
                  />
                </GridItem>

                <Grid style={{width: '36.7rem', marginBottom: '2rem'}}>
                  {form.controls.viewers.value.map((user: UserDTO) => (
                    <UserCard
                      style={{marginBottom: '0.8rem'}}
                      label="Model Viewer"
                      user={user}
                      key={user.id}
                      actions={
                        <>
                          <IconButton
                            style={{float: 'right'}}
                            aria-label="Remove"
                            size="small"
                            onClick={() => handleDelete(user, 'viewers')}
                            {...tid('user-card-remove', user.id)}
                          >
                            <CloseIcon />
                          </IconButton>
                          {webModel && !disableUserSettings && (
                            <span style={{display: data.model.uuid ? 'inline' : 'none'}}>
                              <Button
                                variant="outlined"
                                color="primary"
                                //className="MuiButtonBase-root MuiButton-root MuiButton-outlined MuiButton-outlinedPrimary MuiButton-outlinedSizeSmall MuiButton-sizeSmall"
                                onClick={(event) => openModal(event, user)}
                                style={{display: 'flow-root', fontSize: '1.36rem'}}
                              >
                                User Settings
                              </Button>

                              {Modal}
                            </span>
                          )}
                        </>
                      }
                    />
                  ))}
                </Grid>
              </Grid>
              {webModel && (
                <Grid container direction="column" style={{paddingRight: '3rem'}} xs={12} md={6}>
                  <Grid>
                    <ActionHeader label="Grant">
                      Calibration Management
                      <br />
                      Permissions <small style={{fontSize: 12}}>( for WebModel 2.0 )</small>
                    </ActionHeader>
                  </Grid>
                  <GridItem>
                    <AutocompleteUserControl
                      name="calibrationModelers"
                      label="Calibration Modelers"
                      userFindOptions={userFindOptions}
                      restrictAccessOfExternalUser={restrictAccessOfExternalUser}
                      onSelect={(user) => handleSelectedCalibrationModelers(user as UserDTO)}
                      model={model}
                      AutocompleteProps={{
                        open: calibrationModelerDropdownOpen,
                        onOpen: () => setCalibrationModelerDropdownOpen(true),
                        onClose: () => setCalibrationModelerDropdownOpen(false),
                        disableClearable: true,
                        multiple: true,
                        renderTags: () => null,
                        getOptionDisabled: (option: UserDTO) =>
                          form.controls.modelSteward.value?.some((u: UserDTO) => u.id === option.id) ||
                          form.controls.owners.value?.some((u: UserDTO) => u.id === option.id) ||
                          form.controls.viewers.value?.some((u: UserDTO) => u.id === option.id) ||
                          form.controls.calibrationModelers.value?.some((u: UserDTO) => u.id === option.id),
                        //renderOption: (props, option) => dropdownOption(props, option),
                      }}
                    />
                  </GridItem>

                  <Grid style={{width: '36.7rem', marginBottom: '2rem'}}>
                    {form.controls.calibrationModelers.value.map((user: UserDTO) => (
                      <UserCard
                        style={{marginBottom: '0.8rem'}}
                        label="Calibration Modeler"
                        user={user}
                        key={user.id}
                        actions={
                          !externalModel ? (
                            <>
                              <IconButton
                                style={{float: 'right'}}
                                aria-label="Remove"
                                size="small"
                                onClick={() => handleDelete(user, 'calibrationModelers')}
                                {...tid('user-card-remove', user.id)}
                              >
                                <CloseIcon />
                              </IconButton>
                              {webModel && !disableUserSettings && (
                                <span style={{display: data.model.uuid ? 'inline' : 'none'}}>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    //className="MuiButtonBase-root MuiButton-root MuiButton-outlined MuiButton-outlinedPrimary MuiButton-outlinedSizeSmall MuiButton-sizeSmall"
                                    onClick={(event) => openModal(event, user)}
                                    style={{display: 'flow-root', fontSize: '1.36rem'}}
                                  >
                                    User Settings
                                  </Button>

                                  {Modal}
                                </span>
                              )}
                            </>
                          ) : (
                            <></>
                          )
                        }
                      />
                    ))}
                  </Grid>
                </Grid>
              )}

              {webModel && (
                <Grid container direction="column" style={{paddingRight: '3rem'}} xs={12} md={6}>
                  <Grid>
                    <ActionHeader label="Grant">
                      Scenario Management
                      <br />
                      Permissions <small style={{fontSize: 12}}>( for WebModel 2.0 )</small>
                    </ActionHeader>
                  </Grid>
                  <GridItem>
                    <AutocompleteUserControl
                      name="scenarioModelers"
                      label="Scenario Modelers"
                      userFindOptions={userFindOptions}
                      restrictAccessOfExternalUser={restrictAccessOfExternalUser}
                      onSelect={(user) => handleSelectedScenarioModelers(user as UserDTO)}
                      model={model}
                      AutocompleteProps={{
                        open: scenarioDropdownOpen,
                        onOpen: () => setScenarioDropdownOpen(true),
                        onClose: () => setScenarioDropdownOpen(false),
                        disableClearable: true,
                        multiple: true,
                        renderTags: () => null,
                        getOptionDisabled: (option: UserDTO) =>
                          form.controls.modelSteward.value?.some((u: UserDTO) => u.id === option.id) ||
                          form.controls.owners.value?.some((u: UserDTO) => u.id === option.id) ||
                          form.controls.viewers.value?.some((u: UserDTO) => u.id === option.id) ||
                          form.controls.calibrationModelers.value?.some((u: UserDTO) => u.id === option.id) ||
                          form.controls.scenarioModelers.value?.some((u: UserDTO) => u.id === option.id),
                        //renderOption: (props, option) => dropdownOption(props, option),
                      }}
                    />
                  </GridItem>

                  <Grid style={{width: '36.7rem', marginBottom: '2rem'}}>
                    {form.controls.scenarioModelers.value.map((user: UserDTO) => (
                      <UserCard
                        style={{marginBottom: '0.8rem'}}
                        label="Scenario Modeler"
                        user={user}
                        key={user.id}
                        actions={
                          !externalModel ? (
                            <>
                              <IconButton
                                style={{float: 'right'}}
                                aria-label="Remove"
                                size="small"
                                onClick={() => handleDelete(user, 'scenarioModelers')}
                                {...tid('user-card-remove', user.id)}
                              >
                                <CloseIcon />
                              </IconButton>
                              {webModel && !disableUserSettings && (
                                <span style={{display: data.model.uuid ? 'inline' : 'none'}}>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    //className="MuiButtonBase-root MuiButton-root MuiButton-outlined MuiButton-outlinedPrimary MuiButton-outlinedSizeSmall MuiButton-sizeSmall"
                                    onClick={(event) => openModal(event, user)}
                                    style={{display: 'flow-root', fontSize: '1.36rem'}}
                                  >
                                    User Settings
                                  </Button>

                                  {Modal}
                                </span>
                              )}
                            </>
                          ) : (
                            <></>
                          )
                        }
                      />
                    ))}
                  </Grid>
                </Grid>
              )}

              {webModel && (
                <Grid container direction="column" style={{paddingRight: '3rem'}} xs={12} md={6}>
                  <Grid>
                    <ActionHeader label="Grant">
                      Display User
                      <br />
                      Permissions <small style={{fontSize: 12}}>( for WebModel 2.0 )</small>
                    </ActionHeader>
                  </Grid>
                  <GridItem>
                    <AutocompleteUserControl
                      name="displayViewers"
                      label="Model Display Viewers"
                      userFindOptions={userFindOptions}
                      restrictAccessOfExternalUser={restrictAccessOfExternalUser}
                      onSelect={(user) => handleSelectedDisplayViewers(user as UserDTO)}
                      model={model}
                      AutocompleteProps={{
                        open: displayViewerDropdownOpen,
                        onOpen: () => setDisplayViewerDropdownOpen(true),
                        onClose: () => setDisplayViewerDropdownOpen(false),
                        disableClearable: true,
                        multiple: true,
                        renderTags: () => null,
                        getOptionDisabled: (option: UserDTO) =>
                          form.controls.modelSteward.value?.some((u: UserDTO) => u.id === option.id) ||
                          form.controls.owners.value?.some((u: UserDTO) => u.id === option.id) ||
                          form.controls.displayViewers.value?.some((u: UserDTO) => u.id === option.id),
                        //renderOption: (props, option) => dropdownOption(props, option),
                      }}
                    />
                  </GridItem>

                  <Grid style={{width: '36.7rem', marginBottom: '2rem'}}>
                    {form.controls.displayViewers.value.map((user: UserDTO) => (
                      <UserCard
                        style={{marginBottom: '0.8rem'}}
                        label="Model Display Viewer"
                        user={user}
                        key={user.id}
                        actions={
                          !externalModel ? (
                            <>
                              <IconButton
                                style={{float: 'right'}}
                                aria-label="Remove"
                                size="small"
                                onClick={() => handleDelete(user, 'displayViewers')}
                                {...tid('user-card-remove', user.id)}
                              >
                                <CloseIcon />
                              </IconButton>
                              {webModel && !disableUserSettings && (
                                <span style={{display: data.model.uuid ? 'inline' : 'none'}}>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    //className="MuiButtonBase-root MuiButton-root MuiButton-outlined MuiButton-outlinedPrimary MuiButton-outlinedSizeSmall MuiButton-sizeSmall"
                                    onClick={(event) => openModal(event, user)}
                                    style={{display: 'flow-root', fontSize: '1.36rem'}}
                                  >
                                    User Settings
                                  </Button>

                                  {Modal}
                                </span>
                              )}
                            </>
                          ) : (
                            <></>
                          )
                        }
                      />
                    ))}
                  </Grid>
                </Grid>
              )}
            </Grid>
            {buttons(renderParams)}
          </>
        );
      }}
    />
  );
};

export default ModelEditPermissionsForm;
